import { createReducer, createAction } from '@reduxjs/toolkit';
import { API, SCOPE_STATE_LOCAL_FORECASTS } from '../../constants';
import { application } from '../../services/application';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  extendBuilderWithNotInterestedActions,
  extendListBuilder,
  generateListActions,
  generateNotInterestedActions,
  generateSearchActions,
  INITIAL_LIST_STATE_EXTENDED,
} from '../helpers/listActionsHelpers';
import { onPending, onPendingDone } from '../helpers/sharedCases';

export const INITIAL_STATE = {
  ...INITIAL_LIST_STATE_EXTENDED,
  order: ['postedDate', 'desc'],
  isAllExpanded: false,
  isActiveOpportunitiesSelected: true,
};

const listActions = generateListActions({
  scope: SCOPE_STATE_LOCAL_FORECASTS,
  apiMethod: {
    GET_LIST: API.STATE_LOCAL_FORECASTS.GET_LIST,
    EXPORT_CSV: API.STATE_LOCAL_FORECASTS.EXPORT_CSV,
  },
  getStore: (store) => store.stateLocalForecasts,
});

const notInterestingActions = generateNotInterestedActions({
  scope: SCOPE_STATE_LOCAL_FORECASTS,
  apiMethod: { ADD_NOT_INTERESTED: API.STATE_LOCAL_FORECASTS.ADD_NOT_INTERESTED },
  getStore: (store) => store.stateLocalForecasts,
});

const searchActions = generateSearchActions({
  scope: SCOPE_STATE_LOCAL_FORECASTS,
  apiMethod: {
    SAVE_SEARCH: API.SAVED_SEARCH.CREATE,
    MODIFY_SEARCH: API.SAVED_SEARCH.MODIFY,
    SHARE_SEARCH: API.SHARED_SEARCH.CREATE,
  },
  getStore: (store) => store.stateLocalForecasts,
});

const addToFavorite = createAsyncThunk('slcf/toggleFavorite', (params) => {
  return application.call(API.STATE_LOCAL_FORECASTS.TOGGLE_FAVORITE, params);
});

const getAllNaics = createAsyncThunk('slcf/getAllNaics', (params) => {
  return application.call(API.STATE_LOCAL_FORECASTS.GET_ALL_NAICS, params);
});

const createQuoteRequest = createAsyncThunk('slcf/createQuoteRequest', (params) => {
  return application.call(API.STATE_LOCAL_FORECASTS.CREATE_QUOTE_REQUEST, params);
});

const expandUnexpandStateLocalOpp = createAction('slcf/expandUnexpandStateLocalOpp');
function onExpandUnexpandStateLocalOpp(state, action) {
  const copyItems = [...state.items];
  const index = copyItems.findIndex((i) => i.id === action.payload.id);
  copyItems[index] = { ...copyItems[index], expanded: !copyItems[index]?.expanded };

  return Object.assign(state, {
    items: copyItems,
  });
}

const expandUnexpandAll = createAction('slcf/expandUnexpandAll');
function onExpandUnexpandAll(state, action) {
  const copyItems = [...state.items];
  for (let index = 0; index < copyItems.length; index++) {
    // The following logic (expandedResult) is to avoid changing the expanded value for old items
    let expandedResult = false;
    if (copyItems[index]?.expanded === null) {
      // It is null when the item comes the first time
      expandedResult = state.isAllExpanded;
    } else {
      expandedResult = action.payload?.overrideStatus ? state.isAllExpanded : copyItems[index]?.expanded;
    }
    copyItems[index] = {
      ...copyItems[index],
      expanded: expandedResult,
    };
  }

  return Object.assign(state, {
    items: copyItems,
  });
}

const handleIsAllExpanded = createAction('slcf/handleIsAllExpanded');
function onHandleIsAllExpanded(state, action) {
  return Object.assign(state, {
    isAllExpanded: !state.isAllExpanded,
  });
}

const setIsActiveOpportunitiesSelected = createAction('slcf/setIsActiveOpportunitiesSelected');

const onSetActiveOpportunitiesSelected = (state, action) => {
  return Object.assign(state, {
    isActiveOpportunitiesSelected: action.payload,
  });
};

function onAddToFavorite(state, action) {
  const opportunity = state.items.find((item) => String(item.id) === String(action.payload.id));
  if (opportunity) {
    opportunity.isPotentialPartner = action.payload.isPotentialPartner;
  }
}

const markSeenGovResources = createAsyncThunk('user/markSeenGovResources', () =>
  application.call(API.USERS.MARK_SEEN_GOV_RESOURCES)
);

export const actions = {
  ...listActions,
  ...searchActions,
  ...notInterestingActions,
  getAllNaics,
  createQuoteRequest,
  expandUnexpandStateLocalOpp,
  expandUnexpandAll,
  handleIsAllExpanded,
  setIsActiveOpportunitiesSelected,
  addToFavorite,
  markSeenGovResources,
};

export default createReducer(INITIAL_STATE, (builder) => {
  extendListBuilder(builder, actions)
    .addCase(expandUnexpandStateLocalOpp, onExpandUnexpandStateLocalOpp)
    .addCase(expandUnexpandAll, onExpandUnexpandAll)
    .addCase(handleIsAllExpanded, onHandleIsAllExpanded)
    .addCase(setIsActiveOpportunitiesSelected, onSetActiveOpportunitiesSelected)
    .addCase(addToFavorite.fulfilled, onAddToFavorite);
  extendBuilderWithNotInterestedActions(builder, actions);

  builder
    .addCase(markSeenGovResources.pending, onPending)
    .addCase(markSeenGovResources.fulfilled, (state) => {
      state.pending = false;
    })
    .addCase(markSeenGovResources.rejected, onPendingDone);

  return builder;
});
